/**
 * 三套角色视图（2026-10-30 邢先生定稿，见 ../../工作台业务逻辑.md）
 *
 * ① 城市管理员 (role=city_admin)
 *    只看本市，看业绩（续保/审验/待审验）和本城市车辆数
 *    ❌ 不给看：返利、卖课、其他城市、其他员工
 *
 * ② 财务/保险专员 (role=管理 + rebate:view)
 *    全量钱款（返利到账/未收/卖课）+ 业务量 + 审核
 *    ❌ 不做城市对比、不给看员工工作量
 *
 * ③ 老板 (role=super_admin)
 *    收入 + 城市对比 + 员工工作量（续保/审验/审核三项）
 */

import React from 'react';
import { Row, Col, Card, Tag, Empty, Tooltip } from 'antd';
import {
  RiseOutlined, FallOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CarOutlined, TeamOutlined, SafetyOutlined, DollarOutlined,
  PlayCircleOutlined, ThunderboltOutlined, PlusCircleOutlined,
} from '@ant-design/icons';

// ---------- 工具 ----------
const money = (n) => {
  const v = Number(n || 0);
  if (!v) return '¥0';
  return '¥' + v.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ---------- 角色判定 ----------
export function resolveRole(user) {
  if (!user) return 'unknown';
  const role = user.role || '';
  const perms = user.permissions || [];
  if (role === 'super_admin') return 'boss';
  if (role === 'city_admin') return 'city_admin';
  if ((role === '管理' || role === 'admin') && perms.includes('rebate:view')) return 'finance';
  if (role === '管理' || role === 'admin') return 'finance';
  return 'city_admin';
}

// ---------- 通用：数值卡片 ----------
function StatBox({ title, value, suffix, color = '#5b8ff9', icon, sub, onClick }) {
  return (
    <Card
      size="small"
      hoverable={!!onClick}
      onClick={onClick}
      style={{
        borderRadius: 12,
        border: '1px solid #eef1f6',
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',                       // 同排等高
        transition: 'box-shadow .2s, transform .2s',
        boxShadow: onClick ? '0 1px 3px rgba(0,0,0,.03)' : 'none',
      }}
      styles={{ body: { padding: '16px 18px', height: '100%', display: 'flex', alignItems: 'center' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `linear-gradient(135deg, ${color}, ${color}bb)`,
          color: '#fff', fontSize: 19,
          boxShadow: `0 3px 8px ${color}33`,
        }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: '#8c96a5', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, color, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
            {value}
            {suffix && <span style={{ fontSize: 12, fontWeight: 400, color: '#8c96a5', marginLeft: 4 }}>{suffix}</span>}
          </div>
          {/* 副标题固定占位，避免卡片高度跳动 */}
          <div style={{ fontSize: 11, color: '#b0b7c3', marginTop: 3, height: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {sub || ''}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ---------- 通用：分区标题 ----------
function SectionTitle({ children, extra }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      margin: '24px 0 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 3, height: 14, borderRadius: 2, background: 'linear-gradient(180deg,#5b8ff9,#9254de)' }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2733' }}>{children}</span>
      </div>
      {extra}
    </div>
  );
}

// ============================================================
//  ① 城市管理员视图
// ============================================================
function CityAdminView({ data, city, onJump }) {
  const renew = data?.renew?.total || 0;
  const insp = data?.inspection || {};
  const newIn = data?.new_in || {};
  const newVeh = newIn.vehicle || 0;
  const newDrv = newIn.driver || 0;
  const scopeCity = city || data?.scope?.city || '本市';

  return (
    <div>
      <SectionTitle>本月业绩（{scopeCity}）</SectionTitle>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <StatBox title="本月续保" value={renew} suffix="单" color="#5b8ff9" icon={<SafetyOutlined />}
            sub={data?.renew?.by_type ? `交强${data.renew.by_type['交强险'] || 0} 商业${data.renew.by_type['商业险'] || 0}` : ''}
            onClick={() => onJump && onJump('/insurance?month=' + (data?.month || ''))} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <StatBox title="本月审验" value={insp.done || 0} suffix="台" color="#52c41a" icon={<CheckCircleOutlined />}
            onClick={() => onJump && onJump('/inspections?month=' + (data?.month || ''))} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <StatBox title="本月新增" value={newVeh} suffix="辆"
            color="#eb2f96" icon={<PlusCircleOutlined />}
            sub={`新增司机 ${newDrv} 人`} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <StatBox title="本月新增" value={newVeh} suffix="辆"
            color="#eb2f96" icon={<PlusCircleOutlined />}
            sub={`新增司机 ${newDrv} 人`} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <StatBox title="待审验" value={insp.due || 0} suffix="台" color={(insp.due || 0) > 0 ? '#fa8c16' : '#8c96a5'} icon={<ClockCircleOutlined />}
            sub={(insp.due || 0) > 0 ? '还欠这些' : '暂无'}
            onClick={() => onJump && onJump('/inspections?due=' + (data?.month || ''))} />
        </Col>
      </Row>
    </div>
  );
}

// ============================================================
//  ② 财务/保险专员视图
// ============================================================
function FinanceView({ data, city, onJump }) {
  const reb = data?.rebate || {};
  const _recv = Number(reb.receivable || 0);
  const _recd = Number(reb.received || 0);
  const rebRate = _recv > 0 ? Math.round((_recd / _recv) * 100) : 0;
  const newIn = data?.new_in || {};
  const newVeh = newIn.vehicle || 0;
  const newDrv = newIn.driver || 0;
  const course = data?.course || {};
  const insp = data?.inspection || {};
  const renew = data?.renew?.total || 0;

  return (
    <div>
      {/* ⭐ 2026-10-30 与老板视图统一为「应收→已收→未收」 */}
      {/* ⭐ 2026-11-02 邢先生：「你看首页的应收返利统计口径」
          原：应收取累计值、已收取本月到账、未收=累计−本月 → 三个数字口径不同，相减无意义
          现：本月三件套同一批保单（签单应收 / 已收 / 未收），口径自洽 */}
      <SectionTitle>本月返利（全部城市）</SectionTitle>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <StatBox
            title="本月签单应收"
            value={money(reb.month_amount)}
            color="#1677ff"
            icon={<DollarOutlined />}
            sub={`${reb.month_count || 0} 条保单（按签单日）`}
          />
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <StatBox
            title="本月已收"
            value={money(reb.month_received)}
            color="#52c41a"
            icon={<RiseOutlined />}
            sub={`到账流水 ${money(reb.month_flow_amount)}`}
          />
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <StatBox
            title="本月未收"
            value={money(reb.month_outstanding)}
            color={(reb.month_outstanding || 0) > 0 ? '#fa541c' : '#52c41a'}
            icon={(reb.month_outstanding || 0) > 0 ? <FallOutlined /> : <CheckCircleOutlined />}
            sub={(reb.month_outstanding || 0) > 0 ? '本月这批还没收齐' : '本月已收齐'}
            onClick={() => onJump && onJump('/rebates')}
          />
        </Col>
      </Row>

      <SectionTitle>业务量（全部城市）</SectionTitle>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <StatBox title="本月续保" value={renew} suffix="单" color="#5b8ff9" icon={<SafetyOutlined />}
            onClick={() => onJump && onJump('/insurance?month=' + (data?.month || ''))} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <StatBox title="本月审验" value={insp.done || 0} suffix="台" color="#52c41a" icon={<CheckCircleOutlined />}
            onClick={() => onJump && onJump('/inspections?month=' + (data?.month || ''))} />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <StatBox title="本月新增" value={newVeh} suffix="辆" color="#eb2f96" icon={<PlusCircleOutlined />}
            sub={`新增司机 ${newDrv} 人`} />
        </Col>
      </Row>
    </div>
  );
}

// ============================================================
//  ③ 老板视图
// ============================================================
function BossView({ data, onJump }) {
  const reb = data?.rebate || {};
  const course = data?.course || {};
  const insp = data?.inspection || {};
  const renew = data?.renew?.total || 0;
  const byCity = data?.by_city || [];
  const staff = data?.staff || [];

  const income = Number(reb.month_amount || 0) + Number(course.month_amount || 0);
  // 到账率 = 已收 / 应收（与返利总览同口径）
  const _receivable = Number(reb.receivable || 0);
  const _received = Number(reb.received || 0);
  const rebRate = _receivable > 0 ? Math.round((_received / _receivable) * 100) : 0;
  const maxCity = Math.max(1, ...byCity.map(c => (c.renew || 0) + (c.inspection || 0)));

  return (
    <div>
      {/* ⭐ 2026-10-30 邢先生：「返利总览的内容比首页概况更容易理解」
           → 改口径：应收 → 已收 → 未收 一条链，再看到账率，跟返利总览一致 */}
      {/* ⭐ 2026-11-02 统一为本月三件套（同批保单，口径自洽） */}
      <SectionTitle>本月返利</SectionTitle>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <StatBox
            title="本月签单应收"
            value={money(reb.month_amount)}
            color="#1677ff"
            icon={<DollarOutlined />}
            sub={`${reb.month_count || 0} 条保单（按签单日）`}
          />
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <StatBox
            title="本月已收"
            value={money(reb.month_received)}
            color="#52c41a"
            icon={<RiseOutlined />}
            sub={`到账流水 ${money(reb.month_flow_amount)}`}
          />
        </Col>
        <Col xs={24} sm={8} md={8} lg={8} xl={8}>
          <StatBox
            title="本月未收"
            value={money(reb.month_outstanding)}
            color={(reb.month_outstanding || 0) > 0 ? '#fa541c' : '#52c41a'}
            icon={(reb.month_outstanding || 0) > 0 ? <FallOutlined /> : <CheckCircleOutlined />}
            sub={(reb.month_outstanding || 0) > 0 ? '本月这批还没收齐' : '本月已收齐'}
            onClick={() => onJump && onJump('/rebates')} />
        </Col>
      </Row>

      {/* 到账率进度条（与返利总览同口径） */}
      <Card size="small" style={{ marginTop: 12, borderRadius: 12, border: '1px solid #eef1f6' }}
        styles={{ body: { padding: '12px 18px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#666', fontSize: 13, whiteSpace: 'nowrap' }}>整体到账率</span>
          <div style={{ flex: 1 }}>
            <div style={{
              height: 8, borderRadius: 4, background: '#f0f0f0', overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.min(100, rebRate)}%`, height: '100%',
                background: rebRate >= 100 ? '#52c41a' : (rebRate >= 50 ? '#faad14' : '#ff7875'),
                transition: 'width .4s',
              }} />
            </div>
          </div>
          <span style={{
            fontWeight: 600, fontSize: 14, minWidth: 48, textAlign: 'right',
            color: rebRate >= 100 ? '#52c41a' : (rebRate >= 50 ? '#faad14' : '#ff7875'),
          }}>{rebRate}%</span>
        </div>
      </Card>

      {/* 其他收入 */}
      <SectionTitle>其他收入</SectionTitle>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
          <StatBox title="卖课收入" value={money(course.month_amount)} color="#722ed1" icon={<PlayCircleOutlined />}
            sub={`${course.month_count || 0} 笔`} />
        </Col>
        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
          <StatBox title="本月总收入" value={money(income)} color="#fa8c16" icon={<ThunderboltOutlined />}
            sub={`返利 + 卖课`} />
        </Col>
      </Row>

      {/* 业务量 */}
      <SectionTitle>业务量（全部城市）</SectionTitle>
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
          <StatBox title="本月续保" value={renew} suffix="单" color="#5b8ff9" icon={<SafetyOutlined />}
            sub={data?.renew?.by_type ? `交强${data.renew.by_type['交强险'] || 0} 商业${data.renew.by_type['商业险'] || 0} 承运${data.renew.by_type['承运人责任险'] || 0}` : ''}
            onClick={() => onJump && onJump('/insurance?month=' + (data?.month || ''))} />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
          <StatBox title="本月审验" value={insp.done || 0} suffix="台" color="#52c41a" icon={<CheckCircleOutlined />}
            onClick={() => onJump && onJump('/inspections?month=' + (data?.month || ''))} />
        </Col>
        <Col xs={24} sm={12} md={12} lg={6} xl={6}>
          <StatBox title="待审验" value={insp.due || 0} suffix="台" color="#fa8c16" icon={<ClockCircleOutlined />} />
        </Col>
      </Row>

      {/* 城市对比 */}
      <SectionTitle>城市对比</SectionTitle>
      <Card size="small" style={{ borderRadius: 10, border: '1px solid #eef1f6' }} styles={{ body: { padding: 14 } }}>
        {byCity.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="本月暂无数据" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {byCity.map(c => {
              const total = (c.renew || 0) + (c.inspection || 0);
              const pct = Math.round((total / maxCity) * 100);
              return (
                <div key={c.city}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                    <span style={{ fontWeight: 600, color: '#1f2733' }}>{c.city}</span>
                    <span style={{ color: '#8c96a5' }}>
                      续保 <b style={{ color: '#5b8ff9' }}>{c.renew || 0}</b>
                      <span style={{ margin: '0 6px' }}>·</span>
                      审验 <b style={{ color: '#52c41a' }}>{c.inspection || 0}</b>
                    </span>
                  </div>
                  <div style={{ height: 8, background: '#f0f2f5', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', borderRadius: 4,
                      background: 'linear-gradient(90deg,#5b8ff9,#9254de)',
                      transition: 'width .5s',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* 员工工作量 */}
      <SectionTitle>员工工作量</SectionTitle>
      <Card size="small" style={{ borderRadius: 10, border: '1px solid #eef1f6' }} styles={{ body: { padding: 0 } }}>
        {staff.length === 0 ? <div style={{ padding: 20 }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据" /></div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafbfc' }}>
                <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, color: '#8c96a5', fontSize: 12 }}>员工</th>
                <th style={{ padding: '9px 8px', textAlign: 'center', fontWeight: 600, color: '#8c96a5', fontSize: 12 }}>城市</th>
                <th style={{ padding: '9px 8px', textAlign: 'center', fontWeight: 600, color: '#8c96a5', fontSize: 12 }}>新续保</th>
                <th style={{ padding: '9px 8px', textAlign: 'center', fontWeight: 600, color: '#8c96a5', fontSize: 12 }}>档案补充</th>
                <th style={{ padding: '9px 8px', textAlign: 'center', fontWeight: 600, color: '#8c96a5', fontSize: 12 }}>补录</th>
                <th style={{ padding: '9px 8px', textAlign: 'center', fontWeight: 600, color: '#8c96a5', fontSize: 12 }}>审验</th>
                <th style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 600, color: '#8c96a5', fontSize: 12 }}>审核</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(s => (
                <tr key={s.name} style={{ borderTop: '1px solid #f0f2f5' }}>
                  <td style={{ padding: '9px 14px', fontWeight: 500 }}>
                    <TeamOutlined style={{ color: '#b0b7c3', marginRight: 6 }} />{s.name}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'center' }}>
                    <Tag color={s.city === '潍坊' ? 'blue' : 'green'} style={{ margin: 0, fontSize: 11 }}>{s.city || '-'}</Tag>
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'center', color: '#5b8ff9', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{s.renew || 0}</td>
                  <td style={{ padding: '9px 8px', textAlign: 'center', color: (s.supplement || 0) > 0 ? '#13c2c2' : '#d9d9d9', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {(s.supplement || 0) > 0 ? s.supplement : '-'}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'center', color: (s.backfill || 0) > 0 ? '#fa8c16' : '#d9d9d9', fontVariantNumeric: 'tabular-nums' }}>
                    {(s.backfill || 0) > 0 ? s.backfill : '-'}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'center', color: '#52c41a', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{s.inspection || 0}</td>
                  <td style={{ padding: '9px 14px', textAlign: 'center', color: '#722ed1', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{s.reviewed || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ padding: '8px 14px', borderTop: '1px solid #f0f2f5', fontSize: 11, color: '#b0b7c3', background: '#fafbfc' }}>
          <Tooltip title="业务日 = 签单日（保单上的签单日期）优先，没识别到则用录入日。新续保=本月业务日且非补录（考核口径）；档案补充=补证件/补照片/补司机信息等，一条算一次（2026-11-03 起记录）；补录=11月起日照录入的旧保单，不计入业绩">
            说明：新续保不含补录；档案补充为补材料的工作量（仅老板可见完整版）
          </Tooltip>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
//  主入口
// ============================================================
export default function RoleDashboard({ user, data, city, onJump }) {
  const role = resolveRole(user);

  if (!data) {
    return <Card size="small" style={{ borderRadius: 10 }}><Empty description="本月数据加载中…" /></Card>;
  }

  if (role === 'boss') return <BossView data={data} onJump={onJump} />;
  if (role === 'finance') return <FinanceView data={data} city={city} onJump={onJump} />;
  return <CityAdminView data={data} city={city} onJump={onJump} />;
}
